import { clamp } from '~/lib/utils';
import type { BgColor, RemoveBgOpts } from '~/types/pipeline';

/** Outer color-distance threshold matching {@link removeBackground}'s soft-edge gate. */
export function outerThreshold(tolerance: number, hardEdge: boolean): number {
  return hardEdge ? tolerance : tolerance * 1.4;
}

/**
 * Alpha-key an ImageData against one or more background colors. Returns a new
 * ImageData; the input is never mutated.
 *
 * hardEdge=true  : binary keying — pixels within `tolerance` go to alpha 0,
 *                  beyond untouched.
 * hardEdge=false : two-threshold soft falloff:
 *                  innerT = tolerance * 0.65  — fully transparent below.
 *                  outerT = tolerance * 1.4   — untouched beyond.
 *                  Between: alpha = round(orig * (dist - innerT) / (outerT - innerT)).
 *
 * Distance is the minimum Euclidean RGB distance across all bgColors.
 */
export function removeBackground(image: ImageData, opts: RemoveBgOpts): ImageData {
  const { bgColors, tolerance, hardEdge, floodFill, maxEnclosedIsland } = opts;
  const out = new ImageData(new Uint8ClampedArray(image.data), image.width, image.height);
  const data = out.data;
  const { width: w, height: h } = out;

  const innerT = hardEdge ? tolerance : tolerance * 0.65;
  const outerT = outerThreshold(tolerance, hardEdge);

  // Apply region mask before keying so the loop below sees the reduced alpha
  // and runs unspill on feathered region edges (otherwise bg color fringes
  // remain at boundaries when bgColors is set).
  if (opts.regionMask) {
    const mask = opts.regionMask;
    for (let i = 0, p = 3; i < mask.length; i++, p += 4) {
      const m = mask[i] ?? 0;
      if (m === 0) continue;
      const alpha = data[p] ?? 0;
      data[p] = Math.round(alpha * (1 - m / 255));
    }
  }

  if (bgColors.length > 0) {
    const keyMask = floodFill ? edgeReachableKeyMask(data, w, h, bgColors, outerT) : null;
    if (keyMask && maxEnclosedIsland > 0) {
      mergeSmallEnclosedIslands(data, w, h, bgColors, outerT, keyMask, maxEnclosedIsland);
    }

    for (let i = 0, p = 0; p < data.length; i++, p += 4) {
      if (keyMask && keyMask[i] === 0) continue;
      const { dist, nearest } = minBgDistance(data, p, bgColors);

      const origAlpha = data[p + 3] ?? 0;
      let alphaNew = origAlpha;
      if (dist <= innerT) {
        alphaNew = 0;
      } else if (dist < outerT) {
        const t = (dist - innerT) / (outerT - innerT);
        alphaNew = Math.round(origAlpha * t);
      }
      data[p + 3] = alphaNew;

      // Unspill: invert C_obs = a*C_fg + (1-a)*C_bg → C_fg = (C_obs - (1-a)*C_bg) / a
      if (alphaNew > 0 && alphaNew < 255 && nearest) {
        const r = data[p] ?? 0;
        const g = data[p + 1] ?? 0;
        const b = data[p + 2] ?? 0;
        const a = alphaNew / 255;
        const inv = 1 / a;
        const oneMinusA = 1 - a;
        const fr = (r - oneMinusA * nearest.rgb[0]) * inv;
        const fg = (g - oneMinusA * nearest.rgb[1]) * inv;
        const fb = (b - oneMinusA * nearest.rgb[2]) * inv;
        data[p] = clamp(0, 255, Math.round(fr));
        data[p + 1] = clamp(0, 255, Math.round(fg));
        data[p + 2] = clamp(0, 255, Math.round(fb));
      }
    }
  }

  if (opts.despillStrength > 0 && opts.despillRadius > 0 && bgColors.length > 0) {
    const axis = dominantChromaAxis(bgColors);
    if (axis) despillRGB(out, axis, opts.despillRadius, opts.despillStrength);
  }

  if (opts.erode > 0) {
    erodeAlpha(out, opts.erode);
  }
  if (opts.erode > 0 && opts.feather > 0) {
    featherAlpha(out, opts.feather);
  }

  return out;
}

/** Minimum Euclidean RGB distance from the pixel at byte offset `p` to any
 *  bgColor, plus the nearest match for downstream unspill. */
export function minBgDistance(
  data: Uint8ClampedArray,
  p: number,
  bgColors: RemoveBgOpts['bgColors'],
): { dist: number; nearest: BgColor | undefined } {
  const r = data[p] ?? 0;
  const g = data[p + 1] ?? 0;
  const b = data[p + 2] ?? 0;
  let dist = Infinity;
  let nearest = bgColors[0];
  for (const bg of bgColors) {
    const dr = r - bg.rgb[0];
    const dg = g - bg.rgb[1];
    const db = b - bg.rgb[2];
    const d = Math.sqrt(dr * dr + dg * dg + db * db);
    if (d < dist) {
      dist = d;
      nearest = bg;
    }
  }
  return { dist, nearest };
}

/** BFS from image edges, expanding to any 4-connected pixel whose min RGB
 *  distance to bgColors is ≤ outerT — must include the equality boundary so
 *  hardEdge mode (where innerT === outerT) doesn't leave a ring of pixels
 *  matching the keying predicate but unreachable in the mask. Returns a
 *  per-pixel mask: 1 = should be considered for keying, 0 = leave untouched.
 *  Pixels that color-match bg but aren't edge-reachable through the
 *  bg/falloff region (i.e. fg holes with the bg color) end up 0 and stay
 *  opaque. */
function edgeReachableKeyMask(
  data: Uint8ClampedArray,
  w: number,
  h: number,
  bgColors: RemoveBgOpts['bgColors'],
  outerT: number,
): Uint8Array {
  const N = w * h;
  const mask = new Uint8Array(N);

  const stack: number[] = [];
  const visit = (i: number) => {
    if (mask[i] !== 0) return;
    if (minBgDistance(data, i * 4, bgColors).dist > outerT) return;
    mask[i] = 1;
    stack.push(i);
  };

  for (let x = 0; x < w; x++) {
    visit(x);
    visit((h - 1) * w + x);
  }
  for (let y = 0; y < h; y++) {
    visit(y * w);
    visit(y * w + (w - 1));
  }

  while (stack.length > 0) {
    const i = stack.pop() as number;
    const x = i % w;
    if (x > 0) visit(i - 1);
    if (x < w - 1) visit(i + 1);
    if (i >= w) visit(i - w);
    if (i < N - w) visit(i + w);
  }

  return mask;
}

/** Merges enclosed bg-colored islands of size ≤ maxIslandSize back into the
 *  key mask. Small interior islands are almost always leakage between fg
 *  shapes (e.g. the gap between a sprite's legs), not legitimate holes.
 *  Mutates `mask` in place. */
function mergeSmallEnclosedIslands(
  data: Uint8ClampedArray,
  w: number,
  h: number,
  bgColors: RemoveBgOpts['bgColors'],
  outerT: number,
  mask: Uint8Array,
  maxIslandSize: number,
): void {
  const N = w * h;
  const seen = new Uint8Array(N);
  const stack: number[] = [];
  const component: number[] = [];

  // Mark before the distance check so a "fails the gate" cell isn't re-tested
  // when other seeds reach it from another direction.
  const tryEnqueue = (j: number) => {
    if (seen[j] || mask[j] !== 0) return;
    seen[j] = 1;
    if (minBgDistance(data, j * 4, bgColors).dist > outerT) return;
    stack.push(j);
  };

  for (let start = 0; start < N; start++) {
    tryEnqueue(start);
    if (stack.length === 0) continue;

    // Always drain the BFS so every cell of the component gets seen=1.
    // Otherwise an oversized component reached from two seeds would split
    // into fragments, and a fragment ≤ maxIslandSize would be wrongly merged.
    component.length = 0;
    let oversized = false;
    while (stack.length > 0) {
      const i = stack.pop() as number;
      if (!oversized) {
        component.push(i);
        if (component.length > maxIslandSize) oversized = true;
      }
      const x = i % w;
      if (x > 0) tryEnqueue(i - 1);
      if (x < w - 1) tryEnqueue(i + 1);
      if (i >= w) tryEnqueue(i - w);
      if (i < N - w) tryEnqueue(i + w);
    }

    if (!oversized) for (const i of component) mask[i] = 1;
  }
}

const CHROMA_DOMINANCE_THRESHOLD = 32;

/** Pick the bg axis to despill along: dominant channel of the highest-weight bg
 *  color, gated on a minimum margin over the second-largest channel. Returns
 *  null when the bg is too neutral (white/black/gray) for despill to be useful. */
function dominantChromaAxis(
  bgColors: RemoveBgOpts['bgColors'],
): { dom: 0 | 1 | 2; otherA: 0 | 1 | 2; otherB: 0 | 1 | 2 } | null {
  let top = bgColors[0];
  for (const bg of bgColors) if (bg.weight > (top?.weight ?? -1)) top = bg;
  if (!top) return null;
  const [r, g, b] = top.rgb;
  let dom: 0 | 1 | 2 = 0;
  if (g > r && g >= b) dom = 1;
  else if (b > r && b > g) dom = 2;
  const sorted = [r, g, b].toSorted((a, c) => c - a);
  if ((sorted[0] ?? 0) - (sorted[1] ?? 0) < CHROMA_DOMINANCE_THRESHOLD) return null;
  const otherA = (dom === 0 ? 1 : 0) as 0 | 1 | 2;
  const otherB = (dom === 2 ? 1 : 2) as 0 | 1 | 2;
  return { dom, otherA, otherB };
}

/** Binary "is within `radius` (Chebyshev) of an alpha=0 pixel" mask, computed
 *  as a separable max-filter on the alpha-zero indicator. Output is 1 / 0. */
function nearAlphaZeroMask(img: ImageData, radius: number): Uint8Array {
  const { width: w, height: h, data } = img;
  const flag = new Uint8Array(w * h);
  for (let i = 0, p = 3; i < flag.length; i++, p += 4) {
    flag[i] = (data[p] ?? 0) === 0 ? 1 : 0;
  }
  const tmp = new Uint8Array(w * h);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let m = 0;
      const xMin = Math.max(0, x - radius);
      const xMax = Math.min(w - 1, x + radius);
      for (let xx = xMin; xx <= xMax; xx++) {
        if ((flag[y * w + xx] ?? 0) === 1) {
          m = 1;
          break;
        }
      }
      tmp[y * w + x] = m;
    }
  }
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let m = 0;
      const yMin = Math.max(0, y - radius);
      const yMax = Math.min(h - 1, y + radius);
      for (let yy = yMin; yy <= yMax; yy++) {
        if ((tmp[yy * w + x] ?? 0) === 1) {
          m = 1;
          break;
        }
      }
      flag[y * w + x] = m;
    }
  }
  return flag;
}

/** Suppress the dominant chroma channel toward max(otherA, otherB) on opaque
 *  pixels within `radius` of an alpha=0 pixel. */
function despillRGB(
  img: ImageData,
  axis: { dom: 0 | 1 | 2; otherA: 0 | 1 | 2; otherB: 0 | 1 | 2 },
  radius: number,
  strength: number,
): void {
  const { data } = img;
  const near = nearAlphaZeroMask(img, radius);
  const keep = 1 - strength;
  const { dom, otherA, otherB } = axis;
  for (let i = 0, p = 0; i < near.length; i++, p += 4) {
    if ((data[p + 3] ?? 0) !== 255) continue;
    if ((near[i] ?? 0) === 0) continue;
    const old = data[p + dom] ?? 0;
    const floor = Math.max(data[p + otherA] ?? 0, data[p + otherB] ?? 0);
    if (old <= floor) continue;
    data[p + dom] = clamp(0, 255, Math.round(floor + keep * (old - floor)));
  }
}

function erodeRadius(erode: number): number {
  if (erode <= 0) return 0;
  if (erode >= 2) return 2;
  return 1;
}

/** In-place separable min-filter on alpha only. Treats off-image as alpha=0. */
function erodeAlpha(img: ImageData, erode: number): void {
  const r = erodeRadius(erode);
  if (r === 0) return;
  const { width: w, height: h, data } = img;
  const tmp = new Uint8ClampedArray(w * h);
  // Pass 1: min along x, write to tmp.
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let m = x - r < 0 || x + r >= w ? 0 : 255;
      if (m !== 0) {
        for (let xx = x - r; xx <= x + r; xx++) {
          const a = data[(y * w + xx) * 4 + 3] ?? 0;
          if (a < m) m = a;
        }
      }
      tmp[y * w + x] = m;
    }
  }
  // Pass 2: min along y, write back to data alpha.
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let m = y - r < 0 || y + r >= h ? 0 : 255;
      if (m !== 0) {
        for (let yy = y - r; yy <= y + r; yy++) {
          const a = tmp[yy * w + x] ?? 0;
          if (a < m) m = a;
        }
      }
      data[(y * w + x) * 4 + 3] = m;
    }
  }
}

/** Build a 1-D Gaussian kernel for the given sigma (max half-width 4). */
function gaussianKernel(sigma: number): number[] {
  const half = Math.min(Math.ceil(3 * sigma), 4);
  const k: number[] = [];
  let sum = 0;
  for (let i = -half; i <= half; i++) {
    const v = Math.exp(-(i * i) / (2 * sigma * sigma));
    k.push(v);
    sum += v;
  }
  return k.map((v) => v / sum);
}

/** In-place separable Gaussian on alpha only. */
function featherAlpha(img: ImageData, sigma: number): void {
  const k = gaussianKernel(sigma);
  const half = (k.length - 1) / 2;
  const { width: w, height: h, data } = img;
  const tmp = new Float32Array(w * h);
  // Pass 1: blur along x.
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let acc = 0;
      for (let i = 0; i < k.length; i++) {
        const xx = Math.max(0, Math.min(w - 1, x + i - half));
        acc += (k[i] ?? 0) * (data[(y * w + xx) * 4 + 3] ?? 0);
      }
      tmp[y * w + x] = acc;
    }
  }
  // Pass 2: blur along y, write back to alpha.
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let acc = 0;
      for (let i = 0; i < k.length; i++) {
        const yy = Math.max(0, Math.min(h - 1, y + i - half));
        acc += (k[i] ?? 0) * (tmp[yy * w + x] ?? 0);
      }
      data[(y * w + x) * 4 + 3] = clamp(0, 255, Math.round(acc));
    }
  }
}
